<template>
  <!-- Empty state message -->
  <VRow v-if="categories.length === 0 && isEditable">
    <VCol cols="12">
      <VCard class="text-center py-8 border" variant="text">
        <VIcon icon="mdi-folder-outline" size="64" color="grey-lighten-1" class="mb-4" />
        <VCardTitle class="text-h5">No Categories Yet</VCardTitle>
        <VCardText class="text-body-1 mb-4"> Create your first category to get started </VCardText>
        <VBtn color="primary" @click="openCreateCategoryModal">
          <VIcon start>mdi-plus</VIcon>
          Create Category
        </VBtn>
      </VCard>
    </VCol>
  </VRow>

  <!-- Categories list -->
  <VRow v-else-if="categories.length > 0">
    <VCol cols="12">
      <VCard
        v-for="(category, categoryIndex) in categories"
        :key="category.categoryId"
        class="mb-6 border"
        variant="text"
      >
        <VCardItem>
          <VCardTitle class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <VIcon icon="mdi-folder" class="mr-3" color="primary" />
              <div>
                <div class="text-h6">{{ category.value || 'Unnamed Category' }}</div>
                <div v-if="isEditable" class="text-caption text-medium-emphasis">
                  • Questions: {{ category.questions.length }}
                </div>
              </div>
            </div>

            <div v-if="isEditable" class="d-flex align-center gap-2">
              <VBtn
                icon="mdi-pencil"
                variant="text"
                color="primary"
                @click="openEditCategoryModal(categoryIndex)"
              ></VBtn>

              <VBtn
                icon="mdi-plus"
                variant="text"
                color="success"
                @click="openCreateQuestionModal(categoryIndex)"
              ></VBtn>

              <VBtn
                icon="mdi-delete-outline"
                variant="text"
                color="error"
                @click="openDeleteCategoryModal(categoryIndex)"
              ></VBtn>
            </div>
          </VCardTitle>
        </VCardItem>

        <VCardText>
          <div v-if="category.description">
            <p>{{ category.description }}</p>
          </div>

          <!-- Questions list -->
          <VList lines="two" class="mt-2">
            <div v-for="(question, questionIndex) in category.questions" :key="question.questionId" class="mb-2 border">
              <VListItem>
                <template #prepend>
                  <VAvatar color="primary" variant="tonal" size="36" class="mr-2">
                    {{ questionIndex + 1 }}
                  </VAvatar>
                </template>

                <VListItemTitle class="text-body-1">
                  {{ question.value || 'No text' }}
                </VListItemTitle>
                <template #append>
                  <div v-if="isEditable" class="d-flex align-center gap-1">
                    <VBtn
                      icon="mdi-pencil"
                      variant="text"
                      color="primary"
                      @click="openEditQuestionModal(questionIndex, categoryIndex)"
                    >
                    </VBtn>

                    <VBtn
                      icon="mdi-delete-outline"
                      variant="text"
                      color="error"
                      @click="openDeleteQuestionModal(questionIndex, categoryIndex)"
                    >
                    </VBtn>
                  </div>
                </template>
              </VListItem>
              <SupplierAnswer
                v-if="inviteId && type !== 'Main Category'"
                :invite-id="inviteId"
                :category-id="category.categoryId"
                :questionId="question.questionId"
                :type="type"
                :isPreview="isPreview"
                v-model="question.answer"
              />
              <div v-if="question.fieldType === 'Text' && type === 'Main Category'" class="my-2 mx-2">
                <template v-if="!isPreview">
                  <QuillEditor
                    :content="question.clientAnswer"
                    theme="snow"
                    :placeholder="Enter"
                    style="height: 150px"
                    contentType="html"
                    @update:content="
                      (val) => {
                        question.clientAnswer = val;
                      }
                    "
                  />
                </template>
                <template v-else>
                  <div class="px-2 py-2" v-html="question.clientAnswer"></div>
                </template>
              </div>
              <div v-else-if="question.fieldType === 'Attachment' && type === 'Main Category'" class="my-2 mx-2">
                <DocumentList
                  :ref="
                    (el) => {
                      if (el) documentRefs[question.questionId] = el;
                    }
                  "
                  v-model="question.documents"
                  :recordId="question.questionId"
                  :is-show-delete="!isPreview"
                />
                <template v-if="!isPreview">
                  <UploadDocuments :recordId="question.questionId" @success="handleDocumentsUploaded" />
                </template>
              </div>
            </div>
          </VList>

          <!-- Empty questions state -->
          <div v-if="category.questions.length === 0 && isEditable" class="text-center text-medium-emphasis py-4">
            <VIcon icon="mdi-help-circle-outline" size="80" class="mb-2" />
            <div>No questions in this category yet</div>
            <VBtn variant="text" color="primary" class="mt-2" @click="openCreateQuestionModal(categoryIndex)">
              <VIcon start>mdi-plus</VIcon>
              Add First Question
            </VBtn>
          </div>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>

  <!-- Quick add category button -->
  <VRow v-if="categories.length > 0 && isEditable">
    <VCol cols="12" class="text-center">
      <VBtn variant="outlined" prepend-icon="mdi-plus" @click="openCreateCategoryModal"> Add Another Category </VBtn>
    </VCol>
  </VRow>

  <!-- Category modals -->
  <CreateCategoryModal
    v-model="isShowAddCategoryModal"
    :type="type"
    :requestId="requestId"
    @saved="handleCategoryCreated"
  />

  <EditCategoryModal
    v-model="isShowEditCategoryModal"
    :category="categoryToEdit"
    :requestId="requestId"
    @saved="handleCategoryUpdated"
    @close="resetEditCategoryModal"
  />

  <DeleteCategoryModal
    v-model="isShowDeleteCategoryModal"
    :category="categoryToDelete"
    :requestId="requestId"
    @deleted="deleteCategory"
    @close="resetDeleteCategoryModal"
  />

  <!-- Question modals -->
  <CreateQuestionModal
    v-model="isShowAddQuestionModal"
    :category="categoryToAddQuestion"
    :requestId="requestId"
    :type="type"
    @saved="handleQuestionCreated"
    @close="resetCreateQuestionModal"
  />

  <EditQuestionModal
    v-model="isShowEditQuestionModal"
    :category="categoryToEditQuestion"
    :question="questionToEdit"
    :requestId="requestId"
    @saved="handleQuestionUpdated"
    @close="resetEditQuestionModal"
  />

  <DeleteQuestionModal
    v-model="isShowDeleteQuestionModal"
    :category="categoryToDeleteQuestion"
    :question="questionToDelete"
    :requestId="requestId"
    @deleted="deleteQuestion"
    @close="resetDeleteQuestionModal"
  />
</template>

<script>
import { mapActions } from 'vuex';
// Components
import CreateCategoryModal from '@/components/modals/CreateCategoryModal/CreateCategoryModal.vue';
import EditCategoryModal from '@/components/modals/EditCategoryModal/EditCategoryModal.vue';
import DeleteCategoryModal from '@/components/modals/DeleteCategoryModal/DeleteCategoryModal.vue';

import CreateQuestionModal from '@/components/modals/CreateQuestionModal/CreateQuestionModal.vue';
import EditQuestionModal from '@/components/modals/EditQuestionModal/EditQuestionModal.vue';
import DeleteQuestionModal from '@/components/modals/DeleteQuestionModal/DeleteQuestionModal.vue';
import UploadDocuments from '@/components/common/UploadDocuments/UploadDocuments.vue';
import DocumentList from '@/components/common/DocumentList/DocumentList.vue';

import SupplierAnswer from '../SupplierAnswer/SupplierAnswer.vue';
// Utils
import { formatCategory, formatQuestion } from '@/utils/formatData';
export default {
  name: 'CategoriesWithQuestions',
  components: {
    DocumentList,
    UploadDocuments,
    CreateCategoryModal,
    EditCategoryModal,
    DeleteCategoryModal,
    CreateQuestionModal,
    EditQuestionModal,
    DeleteQuestionModal,
    SupplierAnswer
  },

  props: {
    initialData: {
      type: Array,
      default: () => []
    },
    type: {
      type: String
    },
    requestId: {
      type: String
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    isPreview: {
      type: Boolean,
      default: false
    },
    inviteId: {
      type: String
    }
  },

  data() {
    return {
      loading: false,
      documentRefs: {},
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'], // toggled buttons
          ['blockquote', 'code-block'],
          [{ header: 1 }, { header: 2 }], // custom button values
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ script: 'sub' }, { script: 'super' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ direction: 'rtl' }],
          [{ size: ['small', false, 'large', 'huge'] }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ color: [] }, { background: [] }],
          [{ font: [] }],
          [{ align: [] }],
          ['clean'], // remove formatting
          ['link', 'image', 'video'] // media
        ]
      },
      categories: this.initialData ?? [],
      // Add Category
      isShowAddCategoryModal: false,
      // Delete Category
      isShowDeleteCategoryModal: false,
      categoryToDelete: {},
      // Edit Category
      isShowEditCategoryModal: false,
      categoryToEdit: {},

      // Add Question
      isShowAddQuestionModal: false,
      categoryToAddQuestion: {},
      // Delete Question
      isShowDeleteQuestionModal: false,
      categoryToDeleteQuestion: {},
      questionToDelete: {},
      // Edit Question
      isShowEditQuestionModal: false,
      categoryToEditQuestion: {},
      questionToEdit: {}
    };
  },

  methods: {
    ...mapActions('main', ['showSnackbar', 'startLoading', 'stopLoading']),
    // Documents
    handleDocumentsUploaded(questionId) {
      if (!questionId) {
        return;
      }
      const documentComponent = this.documentRefs[questionId];
      if (documentComponent?.getDocuments) {
        console.log('Refresh Documents', documentComponent);
        documentComponent.getDocuments();
      }
    },
    // Categories - Create
    openCreateCategoryModal() {
      this.isShowAddCategoryModal = true;
    },

    async handleCategoryCreated(createdCategory) {
      this.categories.push(formatCategory(createdCategory));
    },

    // Categories - Edit
    openEditCategoryModal(categoryIndex) {
      const category = this.categories[categoryIndex];
      this.categoryToEdit = category;
      this.isShowEditCategoryModal = true;
    },

    resetEditCategoryModal() {
      this.categoryToEdit = {};
    },

    async handleCategoryUpdated({ categoryId, newValue, description }) {
      const categoryIndex = this.categories.findIndex((category) => category.categoryId === categoryId);
      this.categories[categoryIndex].value = newValue;
      this.categories[categoryIndex].description = description;
    },

    // Categories - Delete
    openDeleteCategoryModal(categoryIndex) {
      const category = this.categories[categoryIndex];
      this.categoryToDelete = category;
      this.isShowDeleteCategoryModal = true;
    },

    resetDeleteCategoryModal() {
      this.categoryToDelete = {};
    },

    async deleteCategory(categoryId) {
      const categoryIndex = this.categories.findIndex((category) => category.categoryId === categoryId);
      this.categories.splice(categoryIndex, 1);
    },

    // Questions - Create
    openCreateQuestionModal(categoryIndex) {
      const category = this.categories[categoryIndex];
      this.categoryToAddQuestion = category;
      this.isShowAddQuestionModal = true;
    },

    resetCreateQuestionModal() {
      this.categoryToAddQuestion = {};
    },

    async handleQuestionCreated({ categoryId, createdQuestion }) {
      const categoryIndex = this.categories.findIndex((category) => category.categoryId === categoryId);
      this.categories[categoryIndex].questions.push(formatQuestion(createdQuestion));
    },

    // Questions - Edit
    openEditQuestionModal(questionIndex, categoryIndex) {
      const category = this.categories[categoryIndex];
      const question = category.questions[questionIndex];

      this.categoryToEditQuestion = category;
      this.questionToEdit = question;
      this.isShowEditQuestionModal = true;
    },

    resetEditQuestionModal() {
      this.categoryToEditQuestion = {};
      this.questionToEdit = {};
    },

    async handleQuestionUpdated({ categoryId, questionId, newValue }) {
      const categoryIndex = this.categories.findIndex((category) => category.categoryId === categoryId);
      const questionIndex = this.categories[categoryIndex].questions.findIndex(
        (question) => question.questionId === questionId
      );
      this.categories[categoryIndex].questions[questionIndex].value = newValue;
    },

    // Questions - Delete
    openDeleteQuestionModal(questionIndex, categoryIndex) {
      const category = this.categories[categoryIndex];
      const question = category.questions[questionIndex];

      this.categoryToDeleteQuestion = category;
      this.questionToDelete = question;
      this.isShowDeleteQuestionModal = true;
    },

    resetDeleteQuestionModal() {
      this.categoryToDeleteQuestion = {};
      this.questionToDelete = {};
    },

    async deleteQuestion({ categoryId, questionId }) {
      const categoryIndex = this.categories.findIndex((category) => category.categoryId === categoryId);
      const questionIndex = this.categories[categoryIndex].questions.findIndex(
        (question) => question.questionId === questionId
      );
      this.categories[categoryIndex].questions.splice(questionIndex, 1);
    }
  }
};
</script>

<style scoped>
.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}
</style>
